/* ═══════════════════════════════════════════════════════════════════════════════
   TourTooltip: Step content card with animated entrance
   ═══════════════════════════════════════════════════════════════════════════════ */

import { motion } from 'framer-motion';
import { TourProgress } from './TourProgress';
import type { TourStep } from '../../lib/tours/types';

interface TourTooltipProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  style: React.CSSProperties;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

export function TourTooltip({
  step,
  currentStep,
  totalSteps,
  style,
  onNext,
  onPrev,
  onSkip,
}: TourTooltipProps) {
  return (
    <motion.div
      className="tour-tooltip"
      style={style}
      initial={{ opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      {/* Progress bar */}
      <TourProgress currentStep={currentStep} totalSteps={totalSteps} />

      {/* Header */}
      <div className="tour-tooltip-header">
        {step.icon && <span className="tour-tooltip-icon">{step.icon}</span>}
        <h3 className="tour-tooltip-title">{step.title}</h3>
      </div>

      {/* Body */}
      <p className="tour-tooltip-description">{step.description}</p>

      {/* Actions */}
      <div className="tour-tooltip-actions">
        <button className="tour-btn-skip" onClick={onSkip}>
          Skip
        </button>
        <div className="tour-tooltip-nav">
          {currentStep > 1 && (
            <button className="tour-btn-secondary" onClick={onPrev}>
              ← Back
            </button>
          )}
          {step.isInteractive ? (
            <div className="tour-interactive-hint">
              <span className="tour-interactive-dot" />
              {step.actionLabel || 'Click to continue'}
            </div>
          ) : (
            <button className="tour-btn-primary" onClick={onNext}>
              {step.actionLabel || 'Next →'}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
